import type { CSSProperties } from "react";

interface SynqusLogoProps {
  size?: number;
  idPrefix?: string;
  style?: CSSProperties;
  className?: string;
  animated?: boolean;
  /** Show the wordmark text next to the icon */
  showText?: boolean;
  /** "dark" text on light bg, or "light" (white) text on dark bg */
  theme?: "light" | "dark";
}

export default function SynqusLogo({
  size = 36,
  idPrefix = "synqus-logo",
  style = {},
  className,
  animated = true,
  showText = false,
  theme = "light",
}: SynqusLogoProps) {
  const textFill = theme === "light" ? "white" : "#1a1a2e";

  const icon = (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={showText ? {} : { ...style }}
      className={showText ? undefined : className}
    >
      <defs>
        {/* Border — white breathing glow */}
        <linearGradient id={`${idPrefix}_bdr`} x1="0" y1="0" x2="48" y2="48">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#ffffff" />
        </linearGradient>

        {/* Bi-directional white pulse gradients for connections */}
        {[1, 2, 3].map((n) => {
          const durA = [2, 2.2, 2.3][n - 1];
          const durB = [2.4, 2.6, 2.1][n - 1];
          const beginA = [0, 0.7, 1.2][n - 1];
          const beginB = [0, 0.3, 0.5][n - 1];
          return (
            <g key={n}>
              <linearGradient id={`${idPrefix}_p${n}a`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="white" stopOpacity={0} />
                <stop offset="40%" stopColor="white" stopOpacity={0.5} />
                <stop offset="60%" stopColor="white" stopOpacity={0.5} />
                <stop offset="100%" stopColor="white" stopOpacity={0} />
                {animated && (
                  <>
                    <animate attributeName="x1" values="-1;1" dur={`${durA}s`} repeatCount="indefinite" begin={`${beginA}s`} />
                    <animate attributeName="x2" values="0;2" dur={`${durA}s`} repeatCount="indefinite" begin={`${beginA}s`} />
                  </>
                )}
              </linearGradient>
              <linearGradient id={`${idPrefix}_p${n}b`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="white" stopOpacity={0} />
                <stop offset="40%" stopColor="white" stopOpacity={0.35} />
                <stop offset="60%" stopColor="white" stopOpacity={0.35} />
                <stop offset="100%" stopColor="white" stopOpacity={0} />
                {animated && (
                  <>
                    <animate attributeName="x1" values="1;-1" dur={`${durB}s`} repeatCount="indefinite" begin={`${beginB}s`} />
                    <animate attributeName="x2" values="2;0" dur={`${durB}s`} repeatCount="indefinite" begin={`${beginB}s`} />
                  </>
                )}
              </linearGradient>
            </g>
          );
        })}
      </defs>

      {/* Pulsing white border */}
      {animated ? (
        <rect x="0.5" y="0.5" width="47" height="47" rx="15.5" stroke={`url(#${idPrefix}_bdr)`} strokeWidth="1.5" fill="none">
          <animate attributeName="opacity" values="0.3;0.8;0.3" dur="3s" repeatCount="indefinite" calcMode="spline" keySplines="0.4 0 0.6 1;0.4 0 0.6 1" />
        </rect>
      ) : (
        <rect x="0.5" y="0.5" width="47" height="47" rx="15.5" stroke="white" strokeWidth="1.5" fill="none" opacity="0.4" />
      )}

      {/* Deep indigo background */}
      <rect x="2" y="2" width="44" height="44" rx="14" fill="#1e1b4b" />

      {/* Magnetic curved connections — behind people */}
      {/* Connection 1: Top (cyan) ↔ Bottom-left (pink) */}
      <path d="M24 13Q14 22 13 31" stroke="white" strokeWidth="0.6" fill="none" opacity="0.08" />
      {animated && (
        <>
          <path d="M24 13Q14 22 13 31" stroke={`url(#${idPrefix}_p1a)`} strokeWidth="1.5" fill="none" />
          <path d="M24 13Q14 22 13 31" stroke={`url(#${idPrefix}_p1b)`} strokeWidth="1.5" fill="none" />
        </>
      )}

      {/* Connection 2: Top (cyan) ↔ Bottom-right (gold) */}
      <path d="M24 13Q34 22 35 31" stroke="white" strokeWidth="0.6" fill="none" opacity="0.08" />
      {animated && (
        <>
          <path d="M24 13Q34 22 35 31" stroke={`url(#${idPrefix}_p2a)`} strokeWidth="1.5" fill="none" />
          <path d="M24 13Q34 22 35 31" stroke={`url(#${idPrefix}_p2b)`} strokeWidth="1.5" fill="none" />
        </>
      )}

      {/* Connection 3: Bottom-left (pink) ↔ Bottom-right (gold) */}
      <path d="M13 32Q24 38 35 32" stroke="white" strokeWidth="0.6" fill="none" opacity="0.08" />
      {animated && (
        <>
          <path d="M13 32Q24 38 35 32" stroke={`url(#${idPrefix}_p3a)`} strokeWidth="1.5" fill="none" />
          <path d="M13 32Q24 38 35 32" stroke={`url(#${idPrefix}_p3b)`} strokeWidth="1.5" fill="none" />
        </>
      )}

      {/* PERSON 1: Top — Cyan */}
      <circle cx="24" cy="8" r="3.2" fill="#22d3ee" />
      <path d="M18.5 16Q24 12.5 29.5 16" stroke="#22d3ee" strokeWidth="2.2" strokeLinecap="round" fill="none" />

      {/* PERSON 2: Bottom-left — Pink */}
      <circle cx="13" cy="28.5" r="3.2" fill="#f472b6" />
      <path d="M7.5 36Q13 32.5 18.5 36" stroke="#f472b6" strokeWidth="2.2" strokeLinecap="round" fill="none" />

      {/* PERSON 3: Bottom-right — Gold */}
      <circle cx="35" cy="28.5" r="3.2" fill="#f59e0b" />
      <path d="M29.5 36Q35 32.5 40.5 36" stroke="#f59e0b" strokeWidth="2.2" strokeLinecap="round" fill="none" />

      {/* No calendar — clean people + connections only */}
    </svg>
  );

  if (!showText) return icon;

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: size * 0.3,
        ...style,
      }}
      className={className}
    >
      {icon}
      {/* Wordmark: Syn[Q-clock]US — one seamless word */}
      <svg
        width={size * 3.8}
        height={size * 1.1}
        viewBox="0 0 310 78"
        fill="none"
      >
        <defs>
          <linearGradient id={`${idPrefix}_wm`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#f43f5e" />
            <stop offset="100%" stopColor="#dc2626" />
          </linearGradient>
          <linearGradient id={`${idPrefix}_qg`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={textFill} />
            <stop offset="100%" stopColor={theme === "light" ? "#cbd5e1" : "#312e81"} />
          </linearGradient>
        </defs>
        {/* "Syn" */}
        <text y="56" fontWeight="800" fontSize="54" letterSpacing="-1.5" fontFamily="'SF Pro Display', Inter, sans-serif" fill={textFill}>
          Syn
        </text>

        {/* Q-clock glyph — equidistant kerning with Syn and US */}
        <g transform="translate(100, 15)">
          {/* Q circle — r=19 matches ~38 unit cap height */}
          <circle cx="19" cy="19" r="19" stroke={`url(#${idPrefix}_qg)`} strokeWidth="4.5" fill="none" />
          {/* Tick marks — cardinal only */}
          <g stroke={theme === "light" ? "rgba(255,255,255,0.35)" : "rgba(30,27,75,0.3)"} strokeWidth="1" strokeLinecap="round">
            <line x1="19" y1="3" x2="19" y2="6.5" />
            <line x1="19" y1="31.5" x2="19" y2="35" />
            <line x1="3" y1="19" x2="6.5" y2="19" />
            <line x1="31.5" y1="19" x2="35" y2="19" />
          </g>
          {/* Hour hand */}
          <line x1="19" y1="19" x2="19" y2="9" stroke={textFill} strokeWidth="2.8" strokeLinecap="round">
            {animated && (
              <animateTransform attributeName="transform" type="rotate"
                values="0 19 19;30 19 19;60 19 19;90 19 19;120 19 19;150 19 19;180 19 19;210 19 19;240 19 19;270 19 19;300 19 19;330 19 19;360 19 19"
                keyTimes="0;0.083;0.167;0.25;0.333;0.417;0.5;0.583;0.667;0.75;0.833;0.917;1"
                calcMode="discrete" dur="24s" repeatCount="indefinite" />
            )}
          </line>
          {/* Minute hand */}
          <line x1="19" y1="19" x2="19" y2="6" stroke={theme === "light" ? "#e2e8f0" : "#312e81"} strokeWidth="1.8" strokeLinecap="round">
            {animated && (
              <animateTransform attributeName="transform" type="rotate"
                values="0 19 19;30 19 19;60 19 19;90 19 19;120 19 19;150 19 19;180 19 19;210 19 19;240 19 19;270 19 19;300 19 19;330 19 19;360 19 19"
                keyTimes="0;0.083;0.167;0.25;0.333;0.417;0.5;0.583;0.667;0.75;0.833;0.917;1"
                calcMode="discrete" dur="2s" repeatCount="indefinite" />
            )}
          </line>
          {/* Center dot */}
          <circle cx="19" cy="19" r="2" fill={textFill} />
          {/* Q tail — short curvy flick */}
          <path d="M30 30Q33 36 37 41" stroke={`url(#${idPrefix}_qg)`} strokeWidth="4.5" strokeLinecap="round" fill="none" />
        </g>

        {/* "US" — equidistant gap from Q right edge */}
        <text x="143" y="56" fontWeight="800" fontSize="54" letterSpacing="-1.5" fontFamily="Outfit, sans-serif" fill={`url(#${idPrefix}_wm)`}>
          US
        </text>
      </svg>
    </div>
  );
}
